/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { lastValueFrom, Observable, Subscription } from 'rxjs';
import { map, startWith } from 'rxjs/operators';
//services
import { EmployeeService } from '../../../../../services/employee.service';
import { WorkflowService } from '../../../../../services/workflow.service';
//models
import { EmployeeModel } from '../../../../../models/employee.model';
import { WorkflowModel } from '../../../../../models/workflow.model';
import { TaskAssignmentType } from '../../../../../enums/task-assignment-type.enum';
//icons
import { ButtonAction, ButtonType } from '@epg/ng-components';
import {
  faPaperclip,
  faSave,
  faUndo,
  faUser,
  faUsers,
  faWarning,
  faX,
  faXmark
} from '@fortawesome/pro-regular-svg-icons';
import { faSortDown } from '@fortawesome/pro-solid-svg-icons';
import {
  FrequencyEnum,
  RecurringDto,
  RecurringOption
} from '../../../../../models/dtos/scheduled-task.dto';
import { EmployeeGroupModel } from '../../../../../models/employee-group.model';
import { TaskReferenceModel } from '../../../../../models/task-reference.model';
import { TaskAttachmentReference, TaskMetaData } from '../../../../../models/task.model';
import { FileService } from '../../../../../services/file/file.service';
import { ScheduledTaskService } from '../../../../../services/scheduled-task.service';
import { SnackbarService } from '../../../../../services/snackbar.service';
import { TaskService } from '../../../../../services/task.service';
import { getWeekdayPositionInMonth } from '../../../../shared/functions';
import { OneAppButtonConfig } from '@/app/shared/oneapp-header/oneapp-header.component';
import { Router } from '@angular/router';

enum RepetitionOptionsEnum {
  DAILY = 'DAILY',
  WEEKLY = 'WEEKLY',
  WEEKDAILY = 'WEEKDAILY',
  MONTHLY = 'MONTHLY',
  YEARLY = 'YEARLY'
}
const SAVE_CALL_BACK_IDENTIFIER = 'save';
const CANCEL_CALL_BACK_IDENTIFIER = 'cancel';
export interface CreateTask {
  name: string;
  workflow_name: string;
  status: string;
  free_workflow_task: boolean;
  task_id: string;
  tags: [];
  assigned_to: string[];
  assigned_to_employee_or_employeegroup: string;
  current_state: number;
  input?: object;
  output?: object;
  temp?: object;
  stack: [];
  claimed_by: string;
  executed_by: string[];
  references: TaskReferenceModel[];
  scheduled_at?: string;
  scheduled_by?: string;
  recurring?: RecurringDto;
  meta_data?: TaskMetaData;
}

@Component({
  selector: 'app-task-manager',
  templateUrl: './task-manager.component.html',
  styleUrls: ['./task-manager.component.scss'],
  standalone: false
})
export class TaskManagerComponent implements OnInit, OnDestroy {
  workflowChangedSubscription: Subscription = new Subscription();
  employeesChangedSubscription: Subscription = new Subscription();
  empGroupChangedSubscription: Subscription = new Subscription();
  schedulingSubscription: Subscription = new Subscription();
  formStatusSubscription: Subscription = new Subscription();

  employeeID = '';
  taskCreationForm = new FormGroup({
    taskNameSelection: new FormControl('', [Validators.required]),
    employeeSelection: new FormControl('', Validators.required),
    workflowSelection: new FormControl('', [
      Validators.required,
      (control: AbstractControl): ValidationErrors | null => {
        if (!control.value) return null;
        if (!this.workflows?.length) return null;
        return this.workflows.some((wf) => wf.name === control.value)
          ? null
          : { invalidWorkflow: true };
      }
    ]),
    referenceSelection: new FormControl(''),

    schedulingSelection: new FormGroup({
      weekdaysControls: new FormGroup({
        MONDAY: new FormControl({ value: false, disabled: true }),
        TUESDAY: new FormControl({ value: false, disabled: true }),
        WEDNESDAY: new FormControl({ value: false, disabled: true }),
        THURSDAY: new FormControl({ value: false, disabled: true }),
        FRIDAY: new FormControl({ value: false, disabled: true }),
        SATURDAY: new FormControl({ value: false, disabled: true }),
        SUNDAY: new FormControl({ value: false, disabled: true })
      }),

      enableScheduling: new FormControl(false),
      scheduleDate: new FormControl({
        value: new Date(new Date().setUTCDate(new Date().getUTCDate() + 1)),
        disabled: true
      }),
      scheduleTime: new FormControl({ value: '00:00', disabled: true }),
      recurring: new FormControl({ value: '', disabled: true }),
      repeatTaskToggle: new FormControl({ value: false, disabled: true }),
      every: new FormControl({ value: 1, disabled: true }), // Control for repetition cycle
      monthlyPatternType: new FormControl({ value: 'position', disabled: true }) // Control for monthly pattern selection
    })
  });

  schedulingSelectionGroup: FormGroup;

  private _workflowInput: WorkflowModel | null = null;

  @Input()
  set workflow(workflow: WorkflowModel | null | undefined) {
    this.applyPresetWorkflow(workflow ?? null);
  }

  get workflow(): WorkflowModel | null {
    return this._workflowInput;
  }
  @Input() dismissSlideInButton: boolean = false;
  @Output() onDismissCallback = new EventEmitter<void>();

  newTask?: CreateTask | any = undefined;
  //debounce var
  timeoutID?: ReturnType<typeof setTimeout>;
  workflows: WorkflowModel[] = [];
  filteredWorkflows!: Observable<WorkflowModel[]>;
  employees: EmployeeModel[] = [];
  //icons
  faUndo = faUndo;
  faUser = faUser;
  faUsers = faUsers;
  faWarning = faWarning;
  faX = faX;
  faPaperclip = faPaperclip;
  saveIndicator: boolean = true;
  employeeGroups: EmployeeGroupModel[] = [];
  employeeAssignmentType: TaskAssignmentType = TaskAssignmentType.EMPLOYEE;
  storeTaskName: string = '';
  referenceName: string = '';
  executableFromMobile: boolean = false;
  showRef: boolean = false;
  isWorkflowLocked: boolean = false;

  minScheduleDate: Date = new Date();
  taskScheduledInPast: boolean = false;
  isSaving: boolean = false;

  execution_days: string[] = [];

  fileErrors: { fileName: string; error: string }[] = [];
  files = [] as File[];
  fileUploadEnabled = true;
  valid_file_extensions = ['.jpg', '.jpeg', '.png', '.pdf'];
  max_file_upload_size = 16; //MB
  max_file_upload_count = 10;

  buttonAction = ButtonAction;
  buttonType = ButtonType;

  saveButtonConfig: OneAppButtonConfig = {
    id: 'save_task',
    disabled: true,
    callBackIdentifier: SAVE_CALL_BACK_IDENTIFIER,
    icon: faSave,
    requiredPermissions: []
  };

  cancelButtonConfig: OneAppButtonConfig = {
    id: 'cancel_task',
    text: $localize`:button text cancel@@buttonCancel:CANCEL`,
    callBackIdentifier: CANCEL_CALL_BACK_IDENTIFIER,
    disabled: this.isSaving,
    icon: faXmark,
    requiredPermissions: []
  };

  constructor(
    private workflowService: WorkflowService,
    private employeeService: EmployeeService,
    private taskService: TaskService,
    private fileService: FileService,
    private snackbarService: SnackbarService,
    private scheduledTaskService: ScheduledTaskService,
    private router: Router
  ) {
    this.schedulingSelectionGroup = this.taskCreationForm.controls.schedulingSelection as FormGroup;
  }

  resetCreationForm(): void {
    this.isSaving = false;
    this.files = [];
    this.fileErrors = [];
    this.employeeID = '';
    this.employeeAssignmentType = TaskAssignmentType.EMPLOYEE;
    this.execution_days = [];
    this.referenceName = '';
    this.executableFromMobile = false;
    this.showRef = false;

    const scheduleDateDefault = new Date(new Date().setUTCDate(new Date().getUTCDate() + 1));

    this.taskCreationForm.reset(
      {
        taskNameSelection: '',
        employeeSelection: '',
        workflowSelection: '',
        referenceSelection: '',
        schedulingSelection: {
          weekdaysControls: {
            MONDAY: false,
            TUESDAY: false,
            WEDNESDAY: false,
            THURSDAY: false,
            FRIDAY: false,
            SATURDAY: false,
            SUNDAY: false
          },
          enableScheduling: false,
          scheduleDate: scheduleDateDefault,
          scheduleTime: '00:00',
          recurring: '',
          repeatTaskToggle: false,
          every: 1,
          monthlyPatternType: 'position'
        }
      },
      { emitEvent: false }
    );

    this.taskCreationForm.controls.workflowSelection.enable({ emitEvent: false });
    this.taskCreationForm.controls.schedulingSelection.controls.weekdaysControls.disable({
      emitEvent: false
    });
    this.taskCreationForm.controls.schedulingSelection.controls.scheduleDate.disable({
      emitEvent: false
    });
    this.taskCreationForm.controls.schedulingSelection.controls.scheduleTime.disable({
      emitEvent: false
    });
    this.taskCreationForm.controls.schedulingSelection.controls.recurring.disable({
      emitEvent: false
    });
    this.taskCreationForm.controls.schedulingSelection.controls.repeatTaskToggle.disable({
      emitEvent: false
    });
    this.taskCreationForm.controls.schedulingSelection.controls.every.disable({ emitEvent: false });
    this.taskCreationForm.controls.schedulingSelection.controls.monthlyPatternType.disable({
      emitEvent: false
    });

    if (this._workflowInput) {
      this.applyPresetWorkflow(this._workflowInput);
    } else {
      this.isWorkflowLocked = false;
      this.updateWorkflowContext();
    }

    this.updateButtonStates();
  }

  private applyPresetWorkflow(workflow: WorkflowModel | null): void {
    const workflowControl = this.taskCreationForm.controls.workflowSelection;
    this._workflowInput = workflow;
    this.isWorkflowLocked = !!workflow;

    if (workflow) {
      this.ensurePresetWorkflowInList(workflow);
      workflowControl.setValue(workflow.name, { emitEvent: false });
      workflowControl.disable({ emitEvent: false });

      if (!this.taskCreationForm.controls.taskNameSelection.dirty) {
        this.taskCreationForm.controls.taskNameSelection.setValue(workflow.name, {
          emitEvent: false
        });
      }

      this.taskCreationForm.controls.referenceSelection.reset('', { emitEvent: false });
      this.updateWorkflowContext(workflow);
    } else {
      workflowControl.enable({ emitEvent: false });
      this.updateWorkflowContext();
    }
  }

  private updateWorkflowContext(workflow?: WorkflowModel): void {
    this.executableFromMobile = workflow?.executableFromMobile ?? false;
    this.showRef = !!workflow?.input;

    if (workflow && this.executableFromMobile) {
      this.getReference(workflow);
    } else {
      this.referenceName = '';
      this.taskCreationForm.controls.referenceSelection.reset('', { emitEvent: false });
    }
  }

  private ensurePresetWorkflowInList(workflow: WorkflowModel): void {
    const exists = this.workflows.some((existingWorkflow) => {
      if (existingWorkflow._id && workflow._id) {
        return existingWorkflow._id === workflow._id;
      }
      if (existingWorkflow.combinedId && workflow.combinedId) {
        return existingWorkflow.combinedId === workflow.combinedId;
      }
      return existingWorkflow.name === workflow.name;
    });

    if (!exists) {
      this.workflows = [workflow, ...this.workflows];
    }
  }
  ngAfterViewInit() {
    this.updateButtonStates();
  }

  private updateButtonStates(): void {
    this.saveButtonConfig.disabled = !this.taskCreationForm.valid || this.isSaving;
    this.cancelButtonConfig.disabled = this.isSaving;
  }

  async ngOnInit(): Promise<void> {
    this.formStatusSubscription = this.taskCreationForm.statusChanges.subscribe(() => {
      this.updateButtonStates();
    });

    // get all workflows
    this.workflowChangedSubscription = this.workflowService.workflowsChanged.subscribe(
      (newWorkflows) => {
        this.workflows = newWorkflows.filter((workflow) => this.isWorkflowFree(workflow));
        if (this._workflowInput) {
          this.ensurePresetWorkflowInList(this._workflowInput);
        }
        this.setFilterOptions();
      }
    );

    // Only fetch workflows if we don't already have a workflow input
    // (e.g., when opened from tasks component vs workflow detail page)
    if (!this._workflowInput) {
      const workflows = await this.workflowService.searchWorkflow({
        search_criteria: {},
        limit: 0,
        skip: 0,
        sort: 'name -version',
        select: '_id name input version initialState executableFromMobile references'
      });
      this.workflows = workflows.filter((workflow) => this.isWorkflowFree(workflow));
      this.setFilterOptions();
    } else {
      // If workflow is provided, initialize the workflows list with it
      this.workflows = [this._workflowInput];
      this.setFilterOptions();
    }
    // get all employees
    this.employeesChangedSubscription = this.employeeService.employeesChanged.subscribe(
      (newEmployees) => {
        this.employees = newEmployees;
      }
    );
    this.employees = await this.employeeService.getEmployees();
    this.employeeGroups = await this.employeeService.getEmployeeGroups();
    this.empGroupChangedSubscription = this.employeeService.employeeGroupsChanged.subscribe(
      (empGroups) => {
        this.employeeGroups = empGroups;
      }
    );
  }

  // Autocomplete function
  setFilterOptions(): void {
    this.filteredWorkflows = this.taskCreationForm.controls.workflowSelection.valueChanges.pipe(
      startWith(''),
      map((value) => this.filter(value || ''))
    );
  }
  private filter(value: string): WorkflowModel[] {
    const filterValue = value;
    return this.workflows.filter((workflow) => workflow.name.includes(filterValue));
  }

  debounceWorkflowCheck(): void {
    if (this.timeoutID) clearTimeout(this.timeoutID);
    this.timeoutID = setTimeout(this.handleWorkflowSearch.bind(this), 300);
  }

  onWorkflowBlur(): void {
    if (this.isWorkflowLocked) return;
    setTimeout(() => {
      if (this.taskCreationForm.controls.workflowSelection.value) {
        this.isValidWorkflowName();
      }
      this.taskCreationForm.controls.workflowSelection.markAsTouched();
    }, 0);
  }

  handleWorkflowSearch(): void {
    if (this.taskCreationForm.controls.workflowSelection.errors !== null) {
      return;
    }
    if (this.isValidWorkflowName()) {
      if (!this.taskCreationForm.controls.taskNameSelection.dirty) {
        this.taskCreationForm.controls.taskNameSelection.setValue(
          this.taskCreationForm.controls.workflowSelection.value
        );
      }
      this.taskCreationForm.controls.referenceSelection.reset();
    }
  }

  clearName(): void {
    if (this.taskCreationForm.controls.workflowSelection.errors !== null) {
      this.taskCreationForm.controls.taskNameSelection.setValue('');
      return;
    }
    if (this.isValidWorkflowName()) {
      if (
        this.taskCreationForm.controls.taskNameSelection.value !=
        this.taskCreationForm.controls.workflowSelection.value
      ) {
        this.taskCreationForm.controls.taskNameSelection.setValue(
          this.taskCreationForm.controls.workflowSelection.value
        );
        this.taskCreationForm.controls.taskNameSelection.markAsPristine();
      }
    }
  }

  isValidWorkflowName(): boolean {
    const workflow = this.getSelectedWorkflow();
    this.executableFromMobile = workflow?.executableFromMobile ?? false;
    this.showRef = !!workflow?.input;

    if (!workflow) {
      return false;
    }

    if (this.executableFromMobile) {
      this.getReference(workflow);
    }

    return true;
  }

  setEmployee(): void {
    this.employeeAssignmentType = TaskAssignmentType.EMPLOYEE;
  }

  setEmployeeGroup(): void {
    this.employeeAssignmentType = TaskAssignmentType.EMPLOYEEGROUP;
  }

  getReference(workflow: WorkflowModel | undefined = this.getSelectedWorkflow()): void {
    this.referenceName = '';
    if (workflow?.input) {
      Object.entries(workflow.input).forEach(([key, value]) => {
        if (key === 'properties') {
          Object.entries(value as object).forEach(([key, value]) => {
            if (key) {
              this.referenceName = key;
            }
          });
        }
      });
    }
  }

  appendToTaskName(): void {
    this.taskCreationForm.controls.taskNameSelection.setValue(
      this.taskCreationForm.controls.taskNameSelection.value +
        ' ' +
        this.taskCreationForm.controls.referenceSelection.value
    );
  }

  addFileUploadError(fileName: string, error: string) {
    this.fileErrors.push({ fileName, error });
  }

  clearFileUploadErrors(): void {
    this.fileErrors = [];
  }

  removeFile(file: File): void {
    const index: number = this.files.indexOf(file, 0);
    this.files.splice(index, 1);
  }

  isMaximumFileCountExceeded(): boolean {
    return this.files.length + 1 > this.max_file_upload_count;
  }

  isFiletypeInvalid(extension: string): boolean {
    return !this.valid_file_extensions.includes(extension.toLowerCase());
  }

  isFileSizeExceedingLimit(filesize: number): boolean {
    return filesize > this.max_file_upload_size * 1000000;
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const files: File[] = [];
      Array.from(input.files).forEach((file) => {
        const extension: string = '.' + (file.name.split('.').pop() ?? '');
        var msg: string = '';
        if (this.isMaximumFileCountExceeded()) {
          msg = $localize`:@@attachmentErrorFilecount:The maximum number of ${this.max_file_upload_count} files has been reached`;
        } else if (this.isFiletypeInvalid(extension)) {
          msg = $localize`:@@attachmentErrorFiletype:File format ${extension} not supported`;
        } else if (this.isFileSizeExceedingLimit(file.size)) {
          msg = $localize`:@@attachmentErrorFilesize:The maximum file size of ${this.max_file_upload_size}MB was exceeded`;
        } else {
          this.files.push(file);
        }
        if (msg) this.addFileUploadError(file.name, msg);
      });
      // Reset the file input so the same file can be selected again if needed
      input.value = '';
    }
  }

  async uploadFiles(): Promise<TaskAttachmentReference[]> {
    let attachments = [] as TaskAttachmentReference[];
    for (let file of this.files) {
      let formData = new FormData();
      formData.set('fileFormat', file.type);
      formData.set('fileName', file.name);
      formData.set(
        'permissions',
        JSON.stringify([
          {
            user: '',
            read: true,
            update: true,
            delete: true,
            updatePermissions: true,
            expires: -1,
            disabled: false
          }
        ])
      );
      // formData.set('description', '');
      // formData.set('tags', '');
      formData.set('application', 'oneapp');
      formData.append('fileContent', file);

      const result: any = await lastValueFrom(this.fileService.sendFormData(formData));
      attachments.push(new TaskAttachmentReference(result.newlyCreatedId, file.name));
    }
    return attachments;
  }

  async addTask(): Promise<void> {
    this.isSaving = true;
    this.updateButtonStates();

    const { assignedTo, claimedBy } = this.buildAssigneeData();
    const selectedWorkflow = this.getSelectedWorkflow();
    if (!selectedWorkflow) {
      this.isValidWorkflowName();
      this.taskCreationForm.controls.workflowSelection.markAsTouched();
      this.isSaving = false;
      this.updateButtonStates();
      return;
    }

    const { references, referenceInput } = this.buildReferencePayload(selectedWorkflow);

    let attachments: TaskAttachmentReference[] = [];
    try {
      attachments = await this.uploadFiles();

      let createTaskModel: CreateTask = {
        name: this.taskCreationForm.controls.taskNameSelection.value!,
        workflow_name: this.taskCreationForm.controls.workflowSelection.value!,
        status: 'NEW',
        free_workflow_task: true,
        task_id: '',
        tags: [],
        assigned_to: assignedTo,
        assigned_to_employee_or_employeegroup: this.getAssignmentTypeKey(),
        current_state: selectedWorkflow.initialState,
        input: referenceInput,
        output: {},
        temp: {},
        stack: [],
        claimed_by: claimedBy,
        executed_by: [],
        references: references,
        meta_data: new TaskMetaData(attachments)
      };

      const schedulingPayload = this.buildSchedulingPayload();
      createTaskModel = { ...createTaskModel, ...schedulingPayload };

      try {
        const newTask = await this.taskService.createTask(createTaskModel);
        this.newTask = newTask;
        this.isSaving = false;
        this.updateButtonStates();

        if (this.dismissSlideInButton) {
          if (this.newTask.scheduled_at && Date.parse(this.newTask.scheduled_at!) > Date.now()) {
            let snackbarActionSubscription: Subscription;
            const snackbarRef = this.snackbarService.showSnackBar(
              $localize`:@@taskSuccessfullyScheduledMessage:Task "${this.newTask.name}" has been scheduled successfully`,
              $localize`:Open a page or dialog@@open:Open`
            );
            snackbarActionSubscription = snackbarRef.onAction().subscribe(() => {
              this.router.navigateByUrl(`home/tasks/scheduled/${this.newTask?.scheduled_by}`);
              snackbarActionSubscription.unsubscribe();
            });
          } else {
            this.snackbarService.showSnackBar(
              $localize`:task created message@@taskSuccessfullyCreatedMessage:Task "${this.newTask.name}" has been created successfully`
            );
          }

          this.resetCreationForm();
          this.onDismissCallback.next();
        } else {
          this.resetCreationForm();
          this.router.navigate(['home', 'tasks', 'activities']);
        }
      } catch (_) {
        for (const attachment of attachments) {
          this.fileService.deleteFile(attachment.file_id).subscribe();
        }
        this.snackbarService.showSnackBar(
          $localize`:task creation error message@@taskCreationError:The task could not be created`,
          '',
          'error'
        );
        this.isSaving = false;
        this.updateButtonStates();
      }
    } catch (_) {
      this.snackbarService.showSnackBar(
        $localize`:file upload error message@@fileUploadError:Failed to upload the attachments`,
        '',
        'error'
      );
      this.isSaving = false;
      this.updateButtonStates();
    }
  }

  getFrequency(repetition: RepetitionOptionsEnum): FrequencyEnum {
    switch (repetition) {
      case RepetitionOptionsEnum.DAILY:
        return FrequencyEnum.DAILY;
      case RepetitionOptionsEnum.WEEKDAILY:
      case RepetitionOptionsEnum.WEEKLY:
        return FrequencyEnum.WEEKLY;
      case RepetitionOptionsEnum.MONTHLY:
        return FrequencyEnum.MONTHLY;
      case RepetitionOptionsEnum.YEARLY:
        return FrequencyEnum.YEARLY;
      default:
        return FrequencyEnum.WEEKLY;
    }
  }

  getExecutionDays(frequency: FrequencyEnum, date?: Date, repetitionCycle?: any): string[] {
    let executionDays: string[] = [];
    switch (frequency) {
      case FrequencyEnum.DAILY:
        //if repetition cycle is greater than 1, return empty array
        if (repetitionCycle > 1) {
          return executionDays;
        }
        this.getExecutionDaysFromFormControl(executionDays);
        return executionDays;

      case FrequencyEnum.WEEKLY:
        this.getExecutionDaysFromFormControl(executionDays);
        return executionDays;

      case FrequencyEnum.MONTHLY:
        const dayOfWeek = this.scheduledTaskService.weekdays.find(
          (day) => day.dayIndex === date?.getDay()
        );
        if (dayOfWeek) {
          executionDays.push(dayOfWeek?.key);
        }

        return executionDays;

      case FrequencyEnum.YEARLY:
        return executionDays;

      default:
        return executionDays;
    }
  }

  private getExecutionDaysFromFormControl(executionDays: string[]) {
    for (const executionDayControl in this.taskCreationForm.controls.schedulingSelection.controls
      .weekdaysControls.controls) {
      if (
        this.taskCreationForm.controls.schedulingSelection.controls.weekdaysControls.get(
          executionDayControl
        )?.value
      ) {
        executionDays.push(executionDayControl);
      }
    }
  }

  ngOnDestroy(): void {
    this.workflowChangedSubscription.unsubscribe();
    this.empGroupChangedSubscription.unsubscribe();
    this.employeesChangedSubscription.unsubscribe();
    this.schedulingSubscription.unsubscribe();
    this.formStatusSubscription.unsubscribe();
  }

  convertMapToJson(map: Map<string, any>) {
    const obj = Array.from(map).reduce(
      (obj, [key, value]) => Object.assign(obj, { [key]: value }),
      {}
    );
    return obj;
  }
  private buildAssigneeData(): { assignedTo: string[]; claimedBy: string } {
    const employeeId = this.taskCreationForm.controls.employeeSelection.value;
    const assignedTo = employeeId ? [employeeId] : [];
    const claimedBy =
      this.employeeAssignmentType === TaskAssignmentType.EMPLOYEE && employeeId ? employeeId : '';

    return { assignedTo, claimedBy };
  }

  private buildReferencePayload(selectedWorkflow: WorkflowModel): {
    references: TaskReferenceModel[];
    referenceInput: Record<string, any>;
  } {
    if (
      !selectedWorkflow.executableFromMobile ||
      !this.taskCreationForm.controls.referenceSelection.value ||
      this.taskCreationForm.controls.referenceSelection.value === ''
    ) {
      return { references: [], referenceInput: {} };
    }

    const referenceValue = this.taskCreationForm.controls.referenceSelection.value;
    const referenceObj = new TaskReferenceModel(this.referenceName, referenceValue);
    const referenceInputMap = new Map<string, any>();
    referenceInputMap.set(this.referenceName, { type: 'STRING', value: referenceValue });

    return {
      references: [referenceObj],
      referenceInput: this.convertMapToJson(referenceInputMap)
    };
  }

  private isWorkflowFree(workflow: WorkflowModel): boolean {
    if (workflow.executableFromMobile !== undefined) {
      return workflow.executableFromMobile === true;
    }
    return workflow.input === undefined || Object.keys(workflow.input).length === 0;
  }

  private getSelectedWorkflow(): WorkflowModel | undefined {
    if (this.isWorkflowLocked && this._workflowInput) {
      return this._workflowInput;
    }

    return this.workflows.find(
      (workflow) => workflow.name === this.taskCreationForm.value.workflowSelection
    );
  }

  private getAssignmentTypeKey(): string {
    return TaskAssignmentType[this.employeeAssignmentType];
  }

  private buildSchedulingPayload(): Pick<CreateTask, 'scheduled_at' | 'recurring'> {
    if (!this.schedulingSelectionGroup.controls.enableScheduling.value) {
      return {};
    }

    const scheduleTime: string | null = this.schedulingSelectionGroup.controls.scheduleTime.value;
    const scheduleDateControl = this.schedulingSelectionGroup.controls.scheduleDate.value;

    if (!scheduleTime || !scheduleDateControl) {
      return {};
    }

    const [hours, minutes] = scheduleTime.split(':').map((value) => parseInt(value, 10));
    const scheduledDate = new Date(scheduleDateControl);
    scheduledDate.setHours(hours || 0, minutes || 0, 0, 0);

    const payload: Pick<CreateTask, 'scheduled_at' | 'recurring'> = {
      scheduled_at: scheduledDate.toISOString()
    };

    const repeatEnabled = this.schedulingSelectionGroup.controls.repeatTaskToggle.value;
    const recurringValue = this.schedulingSelectionGroup.controls.recurring.value as
      | RepetitionOptionsEnum
      | '';

    if (repeatEnabled && recurringValue) {
      const repetitionCycle = this.schedulingSelectionGroup.controls.every.value;
      const frequency = this.getFrequency(recurringValue);
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      let options: RecurringOption = { every: repetitionCycle };

      if (frequency !== FrequencyEnum.MONTHLY && frequency !== FrequencyEnum.YEARLY) {
        options = {
          ...options,
          execution_days: this.getExecutionDays(frequency, scheduledDate, repetitionCycle)
        };
      } else if (frequency === FrequencyEnum.MONTHLY) {
        const patternType = this.schedulingSelectionGroup.controls.monthlyPatternType.value;
        const executionDays = this.getExecutionDays(frequency, scheduledDate);

        if (patternType === 'position') {
          options = {
            ...options,
            execution_day_position: getWeekdayPositionInMonth(scheduledDate),
            execution_days: executionDays
          };
        } else if (patternType === 'fourth' || patternType === 'last') {
          options = {
            ...options,
            execution_day_position: patternType,
            execution_days: executionDays
          };
        }
      }

      payload.recurring = {
        frequency,
        options,
        timezone
      };
    }

    return payload;
  }
  validateReferenceValue(): boolean {
    const referenceValue = this.taskCreationForm.controls.referenceSelection.value || '';
    if (referenceValue === '' || referenceValue == null || !referenceValue) {
      return true;
    } else if (referenceValue.trim() == '') {
      return true;
    } else {
      return false;
    }
  }
  onActionButtonClick(buttonText: string) {
    if (buttonText === SAVE_CALL_BACK_IDENTIFIER) {
      this.addTask();
    }

    if (buttonText === CANCEL_CALL_BACK_IDENTIFIER) {
      this.resetCreationForm();
      if (this.dismissSlideInButton) {
        this.onDismissCallback.next();
      } else {
        this.router.navigate(['home', 'tasks', 'activities']);
      }
    }
  }
}
